import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    AutoComplete,
    Badge,
    Breadcrumb,
    Button,
    Card,
    Col,
    DatePicker,
    Empty,
    InputNumber,
    Layout,
    Modal,
    Row,
    Space,
    Spin,
    Table,
    Tag,
    Typography,
    message,
} from "antd";
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    LabelList,
    ResponsiveContainer,
    Tooltip as ChartTooltip,
    XAxis,
    YAxis,
} from "recharts";
import {
    CalendarOutlined,
    FilterOutlined,
    MinusCircleOutlined,
    PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {Link} from "react-router-dom";
import Api from "../api";
import {useTheme} from "../App";
import {
    formatCompactNumber,
} from "../components/charts/dashboard-chart-utils";

dayjs.extend(utc);

/**
 * API occurred_at is UTC wall clock; offset suffix (+03:00) is not a second conversion.
 */
const parseOccurredAt = (value) => {
    if (!value) {
        return null;
    }

    const str = String(value).trim();
    const wallClockMatch = str.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})/);

    if (wallClockMatch) {
        return dayjs.utc(wallClockMatch[1]).local();
    }

    return dayjs.utc(str).local();
};

const formatOccurredAt = (value) => {
    const parsed = parseOccurredAt(value);

    return parsed ? parsed.format("DD.MM.YYYY HH:mm:ss") : "—";
};

const getTodayDateString = () => dayjs().format("YYYY-MM-DD");

const getTodayDayjsRange = () => {
    const today = dayjs();

    return [today, today];
};

const {Content} = Layout;
const {Text} = Typography;
const {RangePicker} = DatePicker;

const FILTERS_FORM_ID = "event-analytics-filters-form";

const AUTO_REFRESH_MS = 10_000;

const PER_PAGE = 100;
const PROPERTY_HINTS_PER_PAGE = 100;
const PROPERTY_BREAKDOWN_PER_PAGE = 500;
const MAX_PROPERTY_KEYS = 12;
const MAX_VALUES_PER_PROPERTY = 10;
const TABLE_HEADER_OFFSET = 480;
const CHART_BRAND = "#19AEF0";

const emptySummary = () => ({
    total_events: 0,
    unique_users: 0,
    event_types: 0,
    granularity: "day",
    volume: [],
    top_events: [],
});

const isHourlyGranularity = (granularity) => (
    granularity === "hour" || granularity === "hourly"
);

const getInclusiveDayCount = (from, to) => {
    if (!from || !to) {
        return 0;
    }

    return dayjs(to).startOf("day").diff(dayjs(from).startOf("day"), "day") + 1;
};

const parseSummaryTimestamp = (value) => parseOccurredAt(value);

const formatVolumeLabel = (date, granularity) => {
    const local = parseSummaryTimestamp(date) || (date ? dayjs(date) : null);

    if (!local) {
        return "";
    }

    if (isHourlyGranularity(granularity)) {
        return local.format("HH:mm");
    }

    return local.format("DD.MM");
};

const formatVolumeTooltipWhen = (date, granularity) => {
    const local = parseSummaryTimestamp(date) || (date ? dayjs(date) : null);

    if (!local) {
        return "";
    }

    if (isHourlyGranularity(granularity)) {
        return local.format("DD.MM.YYYY HH:mm");
    }

    return local.format("DD.MM.YYYY");
};

const mapVolumeSeries = (volume, granularity, rangeFrom, rangeTo) => {
    if (isHourlyGranularity(granularity)) {
        const dayCount = getInclusiveDayCount(rangeFrom, rangeTo);
        const singleCalendarDay = dayCount <= 1;

        if (singleCalendarDay) {
            const byHour = {};

            (volume || []).forEach(({date, count}) => {
                const local = parseSummaryTimestamp(date);

                if (!local) {
                    return;
                }

                if (rangeFrom && local.format("YYYY-MM-DD") !== rangeFrom) {
                    return;
                }

                const hour = local.hour();
                byHour[hour] = (byHour[hour] || 0) + (count || 0);
            });

            const firstLocal = volume?.[0]?.date ? parseSummaryTimestamp(volume[0].date) : null;
            const day = rangeFrom
                || firstLocal?.format("YYYY-MM-DD")
                || dayjs().format("YYYY-MM-DD");

            const series = [];

            for (let hour = 0; hour < 24; hour += 1) {
                const date = `${day}T${hour.toString().padStart(2, "0")}:00:00`;

                series.push({
                    date,
                    label: `${hour.toString().padStart(2, "0")}:00`,
                    count: byHour[hour] || 0,
                });
            }

            return series;
        }

        return (volume || [])
            .map(({date, count}) => {
                const local = parseSummaryTimestamp(date);

                return {
                    date: local ? local.format() : date,
                    label: local ? local.format("DD.MM HH:mm") : formatVolumeLabel(date, granularity),
                    count: count || 0,
                };
            })
            .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());
    }

    return (volume || []).map(({date, count}) => {
        const local = parseSummaryTimestamp(date);
        const displayDate = local ? local.format() : date;

        return {
            date: displayDate,
            label: formatVolumeLabel(date, granularity),
            count: count || 0,
        };
    });
};

const mapTopEvents = (topEvents) => (
    (topEvents || []).map(({name, count}) => ({
        fullName: name,
        name: name.length > 28 ? `${name.slice(0, 28)}…` : name,
        count,
    }))
);

const aggregateTopPropertiesByKey = (events) => {
    const byPropertyKey = {};

    (events || []).forEach((event) => {
        const props = event.properties;

        if (!props || typeof props !== "object") {
            return;
        }

        Object.entries(props).forEach(([key, value]) => {
            const valueStr = String(value);

            if (!byPropertyKey[key]) {
                byPropertyKey[key] = {};
            }

            byPropertyKey[key][valueStr] = (byPropertyKey[key][valueStr] || 0) + 1;
        });
    });

    return Object.entries(byPropertyKey)
        .map(([key, valueCounts]) => {
            const values = Object.entries(valueCounts)
                .sort((a, b) => b[1] - a[1])
                .slice(0, MAX_VALUES_PER_PROPERTY)
                .map(([value, count]) => ({
                    key,
                    value,
                    count,
                    fullName: value,
                    name: value.length > 26 ? `${value.slice(0, 26)}…` : value,
                }));

            const totalCount = values.reduce((sum, item) => sum + item.count, 0);

            return {key, values, totalCount};
        })
        .filter((item) => item.values.length > 0)
        .sort((a, b) => b.totalCount - a.totalCount || a.key.localeCompare(b.key))
        .slice(0, MAX_PROPERTY_KEYS);
};

const buildQueryString = ({
    eventName,
    userId,
    from,
    to,
    properties,
    page,
    perPage,
}) => {
    const params = new URLSearchParams();

    if (eventName) {
        params.set("event_name", eventName);
    }

    if (userId) {
        params.set("user_id", String(userId));
    }

    if (from) {
        params.set("from", from);
    }

    if (to) {
        params.set("to", to);
    }

    if (page) {
        params.set("page", String(page));
    }

    if (perPage) {
        params.set("per_page", String(perPage));
    }

    (properties || []).forEach(({key, value}) => {
        const trimmedKey = (key || "").trim();
        const trimmedValue = value === undefined || value === null ? "" : String(value).trim();

        if (trimmedKey && trimmedValue !== "") {
            params.set(`properties[${trimmedKey}]`, trimmedValue);
        }
    });

    return params.toString();
};

const formatPropertiesPreview = (properties) => {
    if (!properties || typeof properties !== "object") {
        return "—";
    }

    const text = JSON.stringify(properties);

    return text.length > 80 ? `${text.slice(0, 80)}…` : text;
};

const emptyFilters = () => ({
    eventName: "",
    userId: null,
    properties: [],
});

const filterDraftSignature = (filters) => JSON.stringify({
    eventName: (filters?.eventName || "").trim(),
    userId: filters?.userId ?? null,
    properties: (filters?.properties || []).map((item) => ({
        key: item?.key || "",
        value: String(item?.value ?? ""),
    })),
});

const extractPropertyHints = (events) => {
    const keys = new Set();
    const valuesByKey = {};

    (events || []).forEach((event) => {
        const props = event.properties;

        if (!props || typeof props !== "object") {
            return;
        }

        Object.entries(props).forEach(([key, value]) => {
            keys.add(key);

            if (!valuesByKey[key]) {
                valuesByKey[key] = new Set();
            }

            valuesByKey[key].add(String(value));
        });
    });

    return {
        keys: [...keys].sort(),
        valuesByKey: Object.fromEntries(
            Object.entries(valuesByKey).map(([key, values]) => [key, [...values].sort()])
        ),
    };
};

const EventAnalytics = () => {
    const {isDarkMode} = useTheme();
    const [dateRange, setDateRange] = useState(getTodayDayjsRange);
    const [pickerOpen, setPickerOpen] = useState(false);

    const [draftFilters, setDraftFilters] = useState(emptyFilters);
    const [appliedFilters, setAppliedFilters] = useState(emptyFilters);

    const [eventNameOptions, setEventNameOptions] = useState([]);
    const [eventNamesLoading, setEventNamesLoading] = useState(false);
    const [propertyHints, setPropertyHints] = useState({keys: [], valuesByKey: {}});
    const [propertyHintsLoading, setPropertyHintsLoading] = useState(false);

    const [loading, setLoading] = useState(false);
    const [events, setEvents] = useState([]);
    const [pagination, setPagination] = useState({current: 1, total: 0, pageSize: PER_PAGE});
    const [summary, setSummary] = useState(emptySummary);
    const [propertyBreakdown, setPropertyBreakdown] = useState([]);
    const [propertyBreakdownLoading, setPropertyBreakdownLoading] = useState(false);

    const [selectedEvent, setSelectedEvent] = useState(null);
    const [tableHeight, setTableHeight] = useState(480);
    const [filtersOpen, setFiltersOpen] = useState(false);

    const appliedQuery = useMemo(() => ({
        ...appliedFilters,
        from: dateRange?.[0]?.format("YYYY-MM-DD") || "",
        to: dateRange?.[1]?.format("YYYY-MM-DD") || "",
    }), [appliedFilters, dateRange]);

    const activeFilterCount = useMemo(() => {
        let count = 0;

        if (appliedFilters.eventName) {
            count += 1;
        }

        if (appliedFilters.userId) {
            count += 1;
        }

        count += (appliedFilters.properties || []).filter(
            (item) => item.key?.trim() && String(item.value ?? "").trim() !== ""
        ).length;

        return count;
    }, [appliedFilters]);

    const loadEventNames = useCallback((searchTerm = "") => {
        setEventNamesLoading(true);

        const query = searchTerm.trim()
            ? `?search=${encodeURIComponent(searchTerm.trim())}`
            : "";

        Api.get(`/admin/v2/analytics/event-names${query}`)
            .then((res) => {
                const names = res.data?.data || [];
                setEventNameOptions(names.map((name) => ({value: name, label: name})));
            })
            .catch(() => message.error("Failed to load event names"))
            .finally(() => setEventNamesLoading(false));
    }, []);

    const loadPropertyHints = useCallback(async (eventName) => {
        const trimmed = (eventName || "").trim();

        if (!trimmed) {
            setPropertyHints({keys: [], valuesByKey: {}});
            return;
        }

        setPropertyHintsLoading(true);

        const query = buildQueryString({
            eventName: trimmed,
            from: dateRange?.[0]?.format("YYYY-MM-DD") || "",
            to: dateRange?.[1]?.format("YYYY-MM-DD") || "",
            page: 1,
            perPage: PROPERTY_HINTS_PER_PAGE,
        });

        try {
            const res = await Api.get(`/admin/v2/analytics/events?${query}`);
            setPropertyHints(extractPropertyHints(res.data?.data || []));
        } catch {
            setPropertyHints({keys: [], valuesByKey: {}});
        } finally {
            setPropertyHintsLoading(false);
        }
    }, [dateRange]);

    const queryGenerationRef = useRef(0);
    const queryInFlightRef = useRef(false);
    const latestRefreshInFlightRef = useRef(false);
    const propertyBreakdownGenRef = useRef(0);

    const loadPropertyBreakdown = useCallback(async (queryBase) => {
        const eventName = (queryBase.eventName || "").trim();

        if (!eventName) {
            setPropertyBreakdown([]);
            setPropertyBreakdownLoading(false);
            return;
        }

        const generation = ++propertyBreakdownGenRef.current;
        setPropertyBreakdownLoading(true);

        const query = buildQueryString({
            ...queryBase,
            page: 1,
            perPage: PROPERTY_BREAKDOWN_PER_PAGE,
        });

        try {
            const res = await Api.get(`/admin/v2/analytics/events?${query}`);

            if (generation !== propertyBreakdownGenRef.current) {
                return;
            }

            setPropertyBreakdown(aggregateTopPropertiesByKey(res.data?.data || []));
        } catch {
            if (generation === propertyBreakdownGenRef.current) {
                setPropertyBreakdown([]);
            }
        } finally {
            if (generation === propertyBreakdownGenRef.current) {
                setPropertyBreakdownLoading(false);
            }
        }
    }, []);

    const refreshLatestEvents = useCallback(async () => {
        if (queryInFlightRef.current || latestRefreshInFlightRef.current) {
            return;
        }

        if (paginationPageRef.current !== 1) {
            return;
        }

        latestRefreshInFlightRef.current = true;

        const queryBase = appliedQueryRef.current;
        const tableQuery = buildQueryString({
            ...queryBase,
            page: 1,
            perPage: PER_PAGE,
        });
        const summaryQuery = buildQueryString({...queryBase});

        try {
            const [tableRes, summaryRes] = await Promise.all([
                Api.get(`/admin/v2/analytics/events?${tableQuery}`),
                Api.get(`/admin/v2/analytics/summary?${summaryQuery}`),
            ]);

            setEvents(tableRes.data?.data || []);
            setPagination((prev) => ({
                ...prev,
                current: 1,
                total: tableRes.data?.meta?.total ?? prev.total,
                pageSize: tableRes.data?.meta?.per_page || PER_PAGE,
            }));
            setSummary(summaryRes.data?.data || emptySummary());

            if ((queryBase.eventName || "").trim()) {
                loadPropertyBreakdown(queryBase);
            }
        } catch {
            // background refresh — ignore errors
        } finally {
            latestRefreshInFlightRef.current = false;
        }
    }, [loadPropertyBreakdown]);

    const runQuery = useCallback(async (queryBase, page = 1, {tableOnly = false} = {}) => {
        const generation = ++queryGenerationRef.current;
        queryInFlightRef.current = true;

        setLoading(true);

        const tableQuery = buildQueryString({...queryBase, page, perPage: PER_PAGE});
        const summaryQuery = buildQueryString({...queryBase});

        try {
            if (tableOnly) {
                const tableRes = await Api.get(`/admin/v2/analytics/events?${tableQuery}`);

                if (generation !== queryGenerationRef.current) {
                    return;
                }

                setEvents(tableRes.data?.data || []);
                setPagination({
                    current: tableRes.data?.meta?.current_page || page,
                    total: tableRes.data?.meta?.total || 0,
                    pageSize: tableRes.data?.meta?.per_page || PER_PAGE,
                });
            } else {
                const [tableRes, summaryRes] = await Promise.all([
                    Api.get(`/admin/v2/analytics/events?${tableQuery}`),
                    Api.get(`/admin/v2/analytics/summary?${summaryQuery}`),
                ]);

                if (generation !== queryGenerationRef.current) {
                    return;
                }

                setEvents(tableRes.data?.data || []);
                setPagination({
                    current: tableRes.data?.meta?.current_page || page,
                    total: tableRes.data?.meta?.total || 0,
                    pageSize: tableRes.data?.meta?.per_page || PER_PAGE,
                });
                setSummary(summaryRes.data?.data || emptySummary());

                if ((queryBase.eventName || "").trim()) {
                    loadPropertyBreakdown(queryBase);
                } else {
                    setPropertyBreakdown([]);
                }
            }
        } catch (err) {
            if (generation !== queryGenerationRef.current) {
                return;
            }

            const validation = err?.response?.data?.errors;

            if (validation) {
                message.error(Object.values(validation).flat().join(" "));
            } else {
                message.error("Failed to load analytics");
            }

            if (!tableOnly) {
                setEvents([]);
                setSummary(emptySummary());
                setPropertyBreakdown([]);
                setPagination({current: 1, total: 0, pageSize: PER_PAGE});
            }
        } finally {
            if (generation === queryGenerationRef.current) {
                queryInFlightRef.current = false;
                setLoading(false);
            }
        }
    }, [loadPropertyBreakdown]);

    const appliedQueryRef = useRef(appliedQuery);
    const paginationPageRef = useRef(pagination.current);
    const filtersOpenRef = useRef(filtersOpen);
    const pickerOpenRef = useRef(pickerOpen);
    const loadingRef = useRef(loading);
    const draftFiltersRef = useRef(draftFilters);
    const appliedFiltersRef = useRef(appliedFilters);
    const activeFilterCountRef = useRef(activeFilterCount);

    appliedQueryRef.current = appliedQuery;
    paginationPageRef.current = pagination.current;
    filtersOpenRef.current = filtersOpen;
    pickerOpenRef.current = pickerOpen;
    loadingRef.current = loading;
    draftFiltersRef.current = draftFilters;
    appliedFiltersRef.current = appliedFilters;
    activeFilterCountRef.current = activeFilterCount;

    const shouldPauseAutoRefresh = useCallback(() => {
        if (activeFilterCountRef.current > 0) {
            return true;
        }

        if (loadingRef.current) {
            return true;
        }

        if (filtersOpenRef.current || pickerOpenRef.current) {
            return true;
        }

        return filterDraftSignature(draftFiltersRef.current)
            !== filterDraftSignature(appliedFiltersRef.current);
    }, []);

    useEffect(() => {
        loadEventNames();
        runQuery(appliedQuery, 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const refresh = () => {
            if (document.visibilityState === "hidden") {
                return;
            }

            if (shouldPauseAutoRefresh()) {
                return;
            }

            refreshLatestEvents();
        };

        const intervalId = window.setInterval(refresh, AUTO_REFRESH_MS);

        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                refresh();
            }
        };

        document.addEventListener("visibilitychange", onVisibilityChange);

        return () => {
            window.clearInterval(intervalId);
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, [refreshLatestEvents, shouldPauseAutoRefresh]);

    useEffect(() => {
        const updateHeight = () => {
            setTableHeight(Math.max(window.innerHeight - TABLE_HEADER_OFFSET, 280));
        };

        updateHeight();
        window.addEventListener("resize", updateHeight);

        return () => window.removeEventListener("resize", updateHeight);
    }, []);

    useEffect(() => {
        if (!filtersOpen) {
            return;
        }

        const eventName = draftFilters.eventName?.trim();

        if (eventName) {
            loadPropertyHints(eventName);
        } else {
            setPropertyHints({keys: [], valuesByKey: {}});
        }
    }, [dateRange, filtersOpen, draftFilters.eventName, loadPropertyHints]);

    const chartStats = useMemo(() => ({
        volumeSeries: mapVolumeSeries(
            summary.volume,
            summary.granularity,
            appliedQuery.from,
            appliedQuery.to,
        ),
        hourly: isHourlyGranularity(summary.granularity),
        topEvents: mapTopEvents(summary.top_events),
        uniqueUsers: summary.unique_users || 0,
        eventTypes: summary.event_types || 0,
        totalEvents: summary.total_events || 0,
    }), [summary, appliedQuery.from, appliedQuery.to]);

    const applyEventNameFilter = (name) => {
        if (!name) {
            return;
        }

        const next = {
            eventName: name,
            userId: appliedFilters.userId,
            properties: appliedFilters.properties || [],
        };

        setDraftFilters(next);
        setAppliedFilters(next);
        runQuery({
            ...next,
            from: appliedQuery.from,
            to: appliedQuery.to,
        }, 1);
        loadPropertyHints(name);
        setFiltersOpen(false);
    };

    const applyDateRange = (startDate, endDate) => {
        setDateRange([dayjs(startDate), dayjs(endDate)]);
        runQuery({
            ...appliedFilters,
            from: startDate,
            to: endDate,
        }, 1);
    };

    const handleRangeChange = (dates, dateStrings) => {
        if (dates?.[0] && dates?.[1]) {
            applyDateRange(dateStrings[0], dateStrings[1]);
            setPickerOpen(false);
        }
    };

    const commitFilters = (filters) => {
        const synced = {
            eventName: filters.eventName?.trim() || "",
            userId: filters.userId ?? null,
            properties: (filters.properties || []).map((item) => ({...item})),
        };

        setAppliedFilters(synced);
        setDraftFilters(synced);

        if (!synced.eventName) {
            setPropertyHints({keys: [], valuesByKey: {}});
            setPropertyBreakdown([]);
        }

        runQuery({
            ...synced,
            from: appliedQuery.from,
            to: appliedQuery.to,
        }, 1);
    };

    const applyDraftFilters = () => {
        commitFilters(draftFilters);
    };

    const applyPropertyBarFilter = (key, value) => {
        if (!key) {
            return;
        }

        const valueStr = String(value ?? "");
        const existing = appliedFilters.properties || [];
        const withoutKey = existing.filter((item) => item.key !== key);

        commitFilters({
            ...appliedFilters,
            properties: [...withoutKey, {key, value: valueStr}],
        });
    };

    const submitDraftFilters = (event) => {
        event.preventDefault();
        applyDraftFilters();
        setFiltersOpen(false);
    };

    const resetFilters = () => {
        const cleared = emptyFilters();
        setDraftFilters(cleared);
        setAppliedFilters(cleared);
        const today = getTodayDateString();
        applyDateRange(today, today);
    };

    const volumeHasAnyCount = chartStats.volumeSeries.some((point) => point.count > 0);
    const hasVolume = chartStats.totalEvents > 0 || volumeHasAnyCount;

    const toggleFilters = () => {
        if (!filtersOpen) {
            setDraftFilters({
                ...appliedFilters,
                properties: (appliedFilters.properties || []).map((item) => ({...item})),
            });

        }

        setFiltersOpen((open) => !open);
    };

    const filtersPanel = (
        <form
            id={FILTERS_FORM_ID}
            className="event-analytics-page__filters-panel"
            onSubmit={submitDraftFilters}
        >
            <Row gutter={[12, 12]}>
                <Col xs={24} sm={12}>
                    <Text type="secondary" className="event-analytics-page__label">Event (exact)</Text>
                    <AutoComplete
                        size="small"
                        style={{width: "100%"}}
                        allowClear
                        placeholder="All events"
                        value={draftFilters.eventName || undefined}
                        options={eventNameOptions}
                        onSearch={loadEventNames}
                        onChange={(value) => setDraftFilters((prev) => ({
                            ...prev,
                            eventName: value || "",
                        }))}
                        notFoundContent={eventNamesLoading ? <Spin size="small"/> : null}
                    />
                </Col>
                <Col xs={24} sm={12}>
                    <Text type="secondary" className="event-analytics-page__label">User ID</Text>
                    <InputNumber
                        size="small"
                        style={{width: "100%"}}
                        placeholder="User id"
                        value={draftFilters.userId}
                        onChange={(value) => setDraftFilters((prev) => ({
                            ...prev,
                            userId: value ?? null,
                        }))}
                        min={1}
                        onPressEnter={() => {
                            applyDraftFilters();
                            setFiltersOpen(false);
                        }}
                    />
                </Col>
            </Row>

            {(draftFilters.properties || []).length > 0 && (
                <Space direction="vertical" style={{width: "100%", marginTop: 8}} size={6}>
                    {(draftFilters.properties || []).map((filter, index) => {
                        const valueOptions = (propertyHints.valuesByKey[filter.key] || []).map((value) => ({
                            value,
                        }));

                        return (
                            <Space key={`prop-${index}`} wrap size={6} align="start">
                                <AutoComplete
                                    size="small"
                                    placeholder="Key"
                                    value={filter.key}
                                    disabled={!draftFilters.eventName}
                                    options={propertyHints.keys.map((key) => ({value: key}))}
                                    onChange={(value) => setDraftFilters((prev) => ({
                                        ...prev,
                                        properties: prev.properties.map((item, idx) => (
                                            idx === index ? {...item, key: value || "", value: ""} : item
                                        )),
                                    }))}
                                    style={{width: 140}}
                                    notFoundContent={propertyHintsLoading ? <Spin size="small"/> : null}
                                />
                                <AutoComplete
                                    size="small"
                                    placeholder="Value"
                                    value={filter.value}
                                    disabled={!filter.key}
                                    options={valueOptions}
                                    onChange={(value) => setDraftFilters((prev) => ({
                                        ...prev,
                                        properties: prev.properties.map((item, idx) => (
                                            idx === index ? {...item, value: value || ""} : item
                                        )),
                                    }))}
                                    style={{width: 160}}
                                    notFoundContent={propertyHintsLoading ? <Spin size="small"/> : null}
                                />
                                <Button
                                    type="text"
                                    size="small"
                                    danger
                                    icon={<MinusCircleOutlined/>}
                                    onClick={() => setDraftFilters((prev) => ({
                                        ...prev,
                                        properties: prev.properties.filter((_, idx) => idx !== index),
                                    }))}
                                />
                            </Space>
                        );
                    })}
                </Space>
            )}

            <div className="event-analytics-page__filters-footer">
                <Space size={8} wrap>
                    <Button
                        size="small"
                        htmlType="button"
                        icon={<PlusOutlined/>}
                        onClick={() => {
                            if (!draftFilters.eventName?.trim()) {
                                message.info("Select an event first to use property filters");
                                return;
                            }

                            if ((draftFilters.properties || []).length >= 10) {
                                message.warning("Maximum 10 property filters");
                                return;
                            }

                            setDraftFilters((prev) => ({
                                ...prev,
                                properties: [...(prev.properties || []), {key: "", value: ""}],
                            }));
                        }}
                    >
                        Property
                    </Button>
                    <Button size="small" type="link" htmlType="button" onClick={resetFilters}>
                        Reset all
                    </Button>
                </Space>
                <Button size="small" type="primary" htmlType="submit" loading={loading}>
                    Apply
                </Button>
            </div>
        </form>
    );

    const volumeTooltipEventName = appliedFilters.eventName?.trim() || "All events";
    const showTopProperties = Boolean(appliedFilters.eventName?.trim());

    const propertyChartHeight = (barCount) => Math.max(72, Math.min(barCount * 26 + 16, 220));

    const renderRankedBarChart = (data, {propertyKey, yAxisWidth = 120, height}) => (
        <ResponsiveContainer width="100%" height={height ?? propertyChartHeight(data.length)}>
            <BarChart
                data={data}
                layout="vertical"
                margin={{top: 4, right: 44, left: 4, bottom: 0}}
                barCategoryGap="28%"
            >
                <CartesianGrid strokeDasharray="3 3"/>
                <XAxis type="number" tick={{fontSize: 10}} tickFormatter={formatCompactNumber}/>
                <YAxis
                    type="category"
                    dataKey="name"
                    width={yAxisWidth}
                    tick={{fontSize: 10}}
                />
                <ChartTooltip
                    formatter={(value) => [value, "Count"]}
                    labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ""}
                />
                <Bar
                    dataKey="count"
                    fill={CHART_BRAND}
                    radius={[0, 3, 3, 0]}
                    maxBarSize={10}
                    className="event-analytics-page__bar"
                    onClick={(entry) => {
                        const payload = entry?.payload;

                        if (propertyKey) {
                            applyPropertyBarFilter(propertyKey, payload?.value);
                            return;
                        }

                        const name = payload?.fullName || entry?.fullName;
                        applyEventNameFilter(name);
                    }}
                >
                    <LabelList
                        dataKey="count"
                        position="right"
                        formatter={(value) => formatCompactNumber(value)}
                        style={{fontSize: 11, fill: "#595959"}}
                    />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );

    const renderVolumeTooltip = ({active, payload}) => {
        if (!active || !payload?.length) {
            return null;
        }

        const point = payload[0].payload;
        const count = payload[0].value;
        const when = chartStats.hourly
            ? (point?.date ? dayjs(point.date).format("DD.MM.YYYY HH:mm") : "")
            : formatVolumeTooltipWhen(point?.date, summary.granularity);

        return (
            <div className="event-analytics-page__volume-tooltip recharts-default-tooltip">
                <div className="event-analytics-page__volume-tooltip-event">{volumeTooltipEventName}</div>
                {when && <div className="event-analytics-page__volume-tooltip-when">{when}</div>}
                <div className="event-analytics-page__volume-tooltip-count">
                    Events: {Number(count).toLocaleString()}
                </div>
            </div>
        );
    };

    const columns = [
        {
            title: "Time",
            dataIndex: "occurred_at",
            width: 160,
            render: (value) => (
                <Text code>{formatOccurredAt(value)}</Text>
            ),
        },
        {
            title: "Event",
            dataIndex: "event_name",
            ellipsis: true,
            render: (value) => <Tag color="blue">{value}</Tag>,
        },
        {
            title: "User",
            dataIndex: "user_id",
            width: 100,
            render: (value) => (
                value ? <Link to={`/user/${value}`}>{value}</Link> : "—"
            ),
        },
        {
            title: "Properties",
            dataIndex: "properties",
            ellipsis: true,
            render: (value) => (
                <Text type="secondary" code>{formatPropertiesPreview(value)}</Text>
            ),
        },
    ];

    return (
        <Content className="event-analytics-page" style={{margin: "0 16px"}}>
            <Breadcrumb style={{margin: "16px 0"}}>
                <Breadcrumb.Item>Fitlap</Breadcrumb.Item>
                <Breadcrumb.Item>Event analytics</Breadcrumb.Item>
            </Breadcrumb>

            <div className="event-analytics-page__toolbar site-layout-card">
                <Row align="middle" justify="space-between" gutter={[12, 8]}>
                    <Col flex="auto">
                        <Space wrap align="center" size={8}>
                            <RangePicker
                                size="small"
                                open={pickerOpen}
                                onOpenChange={setPickerOpen}
                                value={dateRange}
                                onChange={handleRangeChange}
                                allowClear={false}
                            />
                            <Button
                                type="text"
                                size="small"
                                icon={<CalendarOutlined/>}
                                aria-label="Open calendar"
                                onClick={() => setPickerOpen(true)}
                            />
                        </Space>
                    </Col>
                    <Col>
                        <Badge count={activeFilterCount} size="small" offset={[-2, 2]}>
                            <Button size="small" icon={<FilterOutlined/>} onClick={toggleFilters}>
                                Filters
                            </Button>
                        </Badge>
                    </Col>
                </Row>

                <div className="event-analytics-page__stats">
                    <div className="event-analytics-page__stat-item">
                        <span className="event-analytics-page__stat-label">Total events</span>
                        <span className="event-analytics-page__stat-value">
                            {loading ? "—" : chartStats.totalEvents.toLocaleString()}
                        </span>
                    </div>
                    <div className="event-analytics-page__stat-item">
                        <span className="event-analytics-page__stat-label">Unique users</span>
                        <span className="event-analytics-page__stat-value">
                            {loading ? "—" : chartStats.uniqueUsers.toLocaleString()}
                        </span>
                    </div>
                    <div className="event-analytics-page__stat-item">
                        <span className="event-analytics-page__stat-label">Event types</span>
                        <span className="event-analytics-page__stat-value">
                            {loading ? "—" : chartStats.eventTypes.toLocaleString()}
                        </span>
                    </div>
                </div>

                {filtersOpen && filtersPanel}

                {activeFilterCount > 0 && (
                    <Space wrap size={4} className="event-analytics-page__chips">
                        {appliedFilters.eventName && (
                            <Tag
                                closable
                                onClose={(event) => {
                                    event.preventDefault();
                                    commitFilters({
                                        ...appliedFilters,
                                        eventName: "",
                                        properties: [],
                                    });
                                }}
                            >
                                {appliedFilters.eventName}
                            </Tag>
                        )}
                        {appliedFilters.userId && (
                            <Tag
                                closable
                                onClose={(event) => {
                                    event.preventDefault();
                                    commitFilters({...appliedFilters, userId: null});
                                }}
                            >
                                User: {appliedFilters.userId}
                            </Tag>
                        )}
                        {(appliedFilters.properties || []).map((item, index) => {
                            const key = item.key?.trim();
                            const value = String(item.value ?? "").trim();

                            if (!key || value === "") {
                                return null;
                            }

                            return (
                                <Tag
                                    key={`filter-prop-${index}-${key}`}
                                    closable
                                    onClose={(event) => {
                                        event.preventDefault();
                                        commitFilters({
                                            ...appliedFilters,
                                            properties: appliedFilters.properties.filter((_, idx) => idx !== index),
                                        });
                                    }}
                                >
                                    {key}: {value}
                                </Tag>
                            );
                        })}
                    </Space>
                )}
            </div>

            <Row gutter={[16, 16]} style={{marginTop: 16}}>
                <Col xs={24} lg={14}>
                    <Card bordered={false} className="dashboard-chart-card site-layout-card" title={chartStats.hourly ? "Event volume (hourly)" : "Event volume"}>
                        {loading ? (
                            <div className="event-analytics-page__chart-loading"><Spin/></div>
                        ) : !hasVolume ? (
                            <Empty description="No events in this range"/>
                        ) : !volumeHasAnyCount ? (
                            <Empty description="No events in this range"/>
                        ) : (
                            <ResponsiveContainer width="100%" height={280}>
                                <AreaChart data={chartStats.volumeSeries}>
                                    <defs>
                                        <linearGradient id="eventVolumeFill" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={CHART_BRAND} stopOpacity={0.35}/>
                                            <stop offset="95%" stopColor={CHART_BRAND} stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3"/>
                                    <XAxis
                                        dataKey="label"
                                        tick={{fontSize: 10}}
                                        minTickGap={16}
                                        interval="preserveStartEnd"
                                    />
                                    <YAxis
                                        width={48}
                                        tick={{fontSize: 10}}
                                        tickFormatter={formatCompactNumber}
                                        allowDecimals={false}
                                    />
                                    <ChartTooltip content={renderVolumeTooltip}/>
                                    <Area
                                        type="monotone"
                                        dataKey="count"
                                        name={volumeTooltipEventName}
                                        stroke={CHART_BRAND}
                                        fill="url(#eventVolumeFill)"
                                        strokeWidth={2}
                                        connectNulls
                                        dot={false}
                                        activeDot={{r: 4}}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </Card>
                </Col>
                <Col xs={24} lg={10}>
                    <Card
                        bordered={false}
                        className="site-layout-card"
                        title={showTopProperties ? "Event properties" : "Top events"}
                        extra={showTopProperties ? (
                            <Space direction="vertical" size={0} align="end">
                                <Text type="secondary" style={{fontSize: 12}}>
                                    Based on last {PROPERTY_BREAKDOWN_PER_PAGE.toLocaleString()} events
                                </Text>
                                <Text type="secondary" style={{fontSize: 12}}>Click bar to filter</Text>
                            </Space>
                        ) : (
                            <Text type="secondary" style={{fontSize: 12}}>Click bar to filter</Text>
                        )}
                    >
                        {loading || (showTopProperties && propertyBreakdownLoading) ? (
                            <div className="event-analytics-page__chart-loading"><Spin/></div>
                        ) : showTopProperties ? (
                            propertyBreakdown.length === 0 ? (
                                <Empty description="No properties in recent sample"/>
                            ) : (
                                <div className="event-analytics-page__property-charts">
                                    {propertyBreakdown.map(({key, values}) => (
                                        <div key={key} className="event-analytics-page__property-chart">
                                            <Text strong className="event-analytics-page__property-chart-key">
                                                {key}
                                            </Text>
                                            {renderRankedBarChart(values, {propertyKey: key, yAxisWidth: 132})}
                                        </div>
                                    ))}
                                </div>
                            )
                        ) : chartStats.topEvents.length === 0 ? (
                            <Empty description="No events to rank"/>
                        ) : (
                            renderRankedBarChart(chartStats.topEvents, {yAxisWidth: 120, height: 280})
                        )}
                    </Card>
                </Col>
            </Row>

            <Card bordered={false} className="site-layout-card" style={{marginTop: 16}} title="Recent events">
                <Table
                    size="small"
                    rowKey="id"
                    loading={loading}
                    columns={columns}
                    dataSource={events}
                    scroll={{y: tableHeight}}
                    pagination={{
                        current: pagination.current,
                        total: pagination.total,
                        pageSize: pagination.pageSize,
                        showSizeChanger: false,
                        showTotal: (total) => `${total.toLocaleString()} events`,
                        onChange: (page) => runQuery(appliedQuery, page, {tableOnly: true}),
                    }}
                    onRow={(record) => ({
                        onClick: () => setSelectedEvent(record),
                        style: {cursor: "pointer"},
                    })}
                />
            </Card>

            <Modal
                title={selectedEvent?.event_name || "Event"}
                open={!!selectedEvent}
                onCancel={() => setSelectedEvent(null)}
                footer={null}
                width={720}
            >
                {selectedEvent && (
                    <Space direction="vertical" size={12} style={{width: "100%"}}>
                        <div>
                            <Text type="secondary">Occurred</Text>
                            <div>{formatOccurredAt(selectedEvent.occurred_at)}</div>
                        </div>
                        <div>
                            <Text type="secondary">User</Text>
                            <div>
                                {selectedEvent.user_id
                                    ? <Link to={`/user/${selectedEvent.user_id}`}>{selectedEvent.user_id}</Link>
                                    : "—"}
                            </div>
                        </div>
                        <div>
                            <Text type="secondary">Properties</Text>
                            <pre
                                className={`event-analytics-page__json${isDarkMode ? " event-analytics-page__json--dark" : ""}`}
                            >
                                {JSON.stringify(selectedEvent.properties || {}, null, 2)}
                            </pre>
                        </div>
                    </Space>
                )}
            </Modal>
        </Content>
    );
};

export default EventAnalytics;
